import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch as Search, FiFilter as Filter } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { isPolicyCurrent } from '../utils/policyStatus';
import PolicyCard from './PolicyCard';

const PoliciesView = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const filteredPolicies = user?.policies?.filter(policy => {
    const matchesSearch = 
      (policy.policyNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (policy.policyType || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (policy.vendor || '').toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesStatus = 
      filterStatus === 'all' || 
      (filterStatus === 'active' && isPolicyCurrent(policy)) ||
      (filterStatus === 'expired' && !isPolicyCurrent(policy));
      
    const matchesType = 
      filterType === 'all' || 
      (policy.policyType || '').toLowerCase().includes(filterType.toLowerCase());
      
    return matchesSearch && matchesStatus && matchesType;
  }) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">My Policies</h1>
        <p className="text-gray-600">
          Manage all your insurance policies in one place
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input 
              type="text" 
              placeholder="Search policies..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC143C] focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <select 
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC143C] focus:border-transparent bg-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <select 
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#DC143C] focus:border-transparent bg-white"
            >
              <option value="all">All Types</option>
              <option value="auto">Auto</option>
              <option value="home">Home</option>
              <option value="commercial">Commercial</option>
              <option value="life">Life</option>
            </select>
          </div>
        </div>
      </div>

      {/* Policy Stats */}
      {/* "Paid Up", "Payment Due" and "Total Due" all came from balanceDue,
          which maps a balance_due column that does not exist -- so every
          client was told all of their policies were paid up. There is no
          billing source in this database; the tiles are gone until there is
          one. Total Policies is a real count and stays. */}
      <div className="grid md:grid-cols-1 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="text-2xl font-bold text-gray-900">{user?.policies?.length || 0}</div>
          <div className="text-sm text-gray-600">Total Policies</div>
        </div>
      </div>

      {/* Policies Grid */}
      {filteredPolicies.length > 0 ? (
        <div className="grid lg:grid-cols-2 gap-6">
          {filteredPolicies.map((policy) => (
            <PolicyCard key={policy.id} policy={policy} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="text-gray-400 mb-4">
            <Filter size={48} className="mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No policies found</h3>
          <p className="text-gray-600">
            {searchTerm || filterStatus !== 'all' || filterType !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Contact us to get started with your insurance coverage!'}
          </p>
          {(!searchTerm && filterStatus === 'all' && filterType === 'all') && (
            <button
              onClick={() => navigate('/quote')}
              className="mt-4 bg-[#DC143C] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#B01030] transition-colors"
            >
              Get a Quote
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PoliciesView;